import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import AppCard from "@plannel/components/ui/AppCard";
import dateUtils, { DATETIME_MINUTE_FORMAT } from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

import AgendaStatusChip from "./AgendaStatusChip";
import AgendaPriorityChip from "./AgendaPriorityChip";
import AgendaTagChip from "./AgendaTagChip";
import AssigneeAvatars from "./AssigneeAvatars";

/**
 * 목록의 카드 한 장. 시안 ①(카드 그리드)의 구성 그대로다.
 *
 * 카드에 담는 것: 상태 · 즐겨찾기 · 제목 · 요약 2줄 · 태그 · 회의시간 · 첨부/댓글 수.
 * 담당자 아바타는 P2 에서 붙는다(지금은 데이터가 없다).
 */
const AgendaCard = ({ agenda, onOpen, onToggleStar, t }) => {
  const isDraft = agenda.statusCd === "DRAFT";

  // 서버 타임스탬프는 반드시 공용 포매터를 거친다. raw new Date(...).toLocaleString() 을 쓰면
  // 문자열이 UTC 로 해석되어 KST 기준 +9시간으로 표시된다 (10:00 회의가 19:00 으로 보인다).
  const meetingLabel = agenda.meetingStartTs
    ? dateUtils.formatDate(agenda.meetingStartTs, DATETIME_MINUTE_FORMAT)
    : t("MSG_MeetingAgendaNoTime");

  return (
    <AppCard
      sx={{
        p: 2.25,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        // Draft 는 아직 남에게 안 보이는 글이라 테두리로 구분한다
        borderStyle: isDraft ? "dashed" : "solid",
        cursor: "pointer",
        "&:hover": { borderColor: "primary.main" }
      }}
      onClick={() => onOpen(agenda)}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AgendaStatusChip statusCd={agenda.statusCd} t={t} />
        <AgendaPriorityChip priorityCd={agenda.priorityCd} t={t} />
        {isDraft && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {t("MSG_MeetingAgendaDraftOnlyYou")}
          </Typography>
        )}
        <Tooltip title={t(agenda.starred ? "MSG_MeetingAgendaUnstar" : "MSG_MeetingAgendaStar")}>
          <IconButton
            size="small"
            sx={{ ml: "auto" }}
            aria-label={t(agenda.starred ? "MSG_MeetingAgendaUnstar" : "MSG_MeetingAgendaStar")}
            onClick={(event) => {
              // 카드 전체가 클릭 대상이라 별을 누를 때 상세로 넘어가면 안 된다
              event.stopPropagation();
              onToggleStar(agenda);
            }}
          >
            {agenda.starred ? (
              <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
            ) : (
              <StarBorderIcon fontSize="small" sx={{ color: "action.disabled" }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        sx={{
          fontSize: FS.TITLE,
          fontWeight: 600,
          color: "text.primary",
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden"
        }}
      >
        {agenda.title}
      </Typography>

      <Typography
        sx={{
          fontSize: FS.BODY,
          color: "text.secondary",
          lineHeight: 1.55,
          minHeight: "2.9em",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden"
        }}
      >
        {agenda.excerpt || ""}
      </Typography>

      {agenda.tags?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {agenda.tags.map((tag) => (
            <AgendaTagChip key={tag} tag={tag} />
          ))}
        </Box>
      )}

      <Box sx={{ height: "1px", bgcolor: "divider", mt: "auto" }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          <Typography
            sx={{
              fontSize: FS.CAPTION,
              fontWeight: agenda.meetingStartTs ? 600 : 400,
              color: agenda.meetingStartTs ? "text.primary" : "text.disabled"
            }}
          >
            {meetingLabel}
          </Typography>
        </Box>

        {agenda.attachmentCount > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <AttachFileIcon sx={{ fontSize: 15, color: "text.disabled" }} />
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
              {agenda.attachmentCount}
            </Typography>
          </Box>
        )}

        {agenda.commentCount > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
              {agenda.commentCount}
            </Typography>
          </Box>
        )}

        {/*
          담당자는 작성자와 자리를 나눈다 — 아바타 두 묶음이 붙어 있으면 누가 쓴 사람이고
          누가 배정된 사람인지 구분할 수 없다. 담당자는 배지들 옆, 작성자는 오른쪽 끝.
        */}
        <AssigneeAvatars assignees={agenda.assignees} max={3} size={22} />

        {agenda.createdUser && (
          <Tooltip title={agenda.createdUser}>
            <Avatar sx={{ ml: "auto", width: 26, height: 26, fontSize: FS.CAPTION }}>
              {agenda.createdUser.trim().charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        )}
      </Box>
    </AppCard>
  );
};

export default AgendaCard;
